import { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@satyrsmc/shared/components";
import { Loader2 } from "lucide-react";

interface ImageUploadCropperProps {
  open: boolean;
  onClose: () => void;
  /** Called with the raw base64 string (no data URL prefix) of the cropped image. */
  onConfirm: (base64: string) => void;
  /** Aspect ratio for the crop area: 1 for avatars, 1.5 (3/2) for bike photos. */
  aspectRatio: number;
  title?: string;
  /** Short tips (angle, appearance, zoom) shown before selection and, condensed, while cropping. */
  guidance?: string[];
  /**
   * Export the crop as lossless PNG instead of JPEG, so a transparent source
   * (a logo, a graphic) keeps its alpha channel through the crop step — JPEG
   * has no alpha channel, so it silently flattens transparency onto black.
   * The server (MediaService) re-encodes to WebP if the upload turns out to
   * actually have transparency, so this only affects the crop step's fidelity,
   * not the final stored format. Off by default: most crops are photos, where
   * PNG's much larger payload isn't worth it for something that gets thrown away.
   */
  preserveTransparency?: boolean;
}

/** Extract the pixel crop from the source image using an off-screen canvas. Exported for testing. */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  preserveTransparency: boolean,
): Promise<string> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );
  // Strips the data URL prefix, leaving just the base64 payload.
  const dataUrl = preserveTransparency
    ? canvas.toDataURL("image/png")
    : canvas.toDataURL("image/jpeg", 0.92);
  return dataUrl.split(",")[1] ?? "";
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Generic image upload + crop dialog.
 * Usage:
 *   <ImageUploadCropper
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onConfirm={(base64) => uploadMutation.mutate({ photo: base64 })}
 *     aspectRatio={1}        // 1:1 for avatars
 *     title="Profile Photo"
 *   />
 */
export function ImageUploadCropper({
  open,
  onClose,
  onConfirm,
  aspectRatio,
  title = "Crop Photo",
  guidance,
  preserveTransparency = false,
}: ImageUploadCropperProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  async function handleConfirm() {
    if (!imageSrc || !croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const base64 = await getCroppedImg(imageSrc, croppedAreaPixels, preserveTransparency);
      onConfirm(base64);
      handleClose();
    } finally {
      setIsProcessing(false);
    }
  }

  function handleClose() {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Select and crop a photo to upload.</DialogDescription>
        </DialogHeader>

        {!imageSrc ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <p className="text-sm text-muted-foreground">Select a photo to crop and upload.</p>
            {guidance && guidance.length > 0 && (
              <div className="w-full rounded-md bg-muted p-3 text-sm text-muted-foreground">
                <p className="mb-1 font-medium text-foreground">Tips for a great photo</p>
                <ul className="list-disc space-y-0.5 pl-4">
                  {guidance.map((tip) => (
                    <li key={tip}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
            <Button type="button" onClick={() => fileInputRef.current?.click()}>
              Choose Photo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        ) : (
          <>
            {guidance && guidance.length > 0 && (
              <p className="text-xs text-muted-foreground">{guidance[0]}</p>
            )}

            {/* Crop area */}
            <div className="relative h-72 w-full overflow-hidden rounded-md bg-black">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom slider */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose} disabled={isProcessing}>
                Cancel
              </Button>
              <Button type="button" onClick={handleConfirm} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-1 size-4 animate-spin" />
                    Processing…
                  </>
                ) : (
                  "Use Photo"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
